import type { LucideIcon } from 'lucide-react';
import {
  User,
  Handshake,
  Target,
  ListChecks,
  FileText,
  FileSignature,
  Megaphone,
  NotebookPen,
  Users,
  Send,
  ReceiptText,
  CalendarDays,
  Sparkles,
  Brain,
  BarChart3,
  Bell,
  Lightbulb,
  PhoneCall,
  ShieldCheck,
  PenLine,
} from 'lucide-react';
import type { EntityType } from '../../types/chat';

export interface EntityVisual {
  Icon: LucideIcon;
  chip: string;
  bar: string;
  icon: string;
}

// Partial: entity types without a bespoke visual fall back to FALLBACK_VISUAL,
// so a new manifest entity type renders sensibly without a change here.
const ENTITY_VISUALS: Partial<Record<EntityType, EntityVisual>> = {
  contact: {
    Icon: User,
    chip: 'bg-sky-500/15 ring-sky-500/25',
    icon: 'text-sky-300',
    bar: 'bg-sky-500',
  },
  // Unified pipeline entity (migration 0055) — supersedes pursuit + engagement.
  opportunity: {
    Icon: Target,
    chip: 'bg-primary/15 ring-primary/25',
    icon: 'text-primary',
    bar: 'bg-primary',
  },
  task: {
    Icon: ListChecks,
    chip: 'bg-emerald-500/15 ring-emerald-500/25',
    icon: 'text-emerald-300',
    bar: 'bg-emerald-500',
  },
  proposal: {
    Icon: FileText,
    chip: 'bg-primary/15 ring-primary/25',
    icon: 'text-primary',
    bar: 'bg-primary',
  },
  contract: {
    Icon: FileSignature,
    chip: 'bg-rose-500/15 ring-rose-500/25',
    icon: 'text-rose-300',
    bar: 'bg-rose-500',
  },
  campaign: {
    Icon: Megaphone,
    chip: 'bg-fuchsia-500/15 ring-fuchsia-500/25',
    icon: 'text-fuchsia-300',
    bar: 'bg-fuchsia-500',
  },
  brief: {
    Icon: NotebookPen,
    chip: 'bg-cyan-500/15 ring-cyan-500/25',
    icon: 'text-cyan-300',
    bar: 'bg-cyan-500',
  },
  talent: {
    Icon: Users,
    chip: 'bg-teal-500/15 ring-teal-500/25',
    icon: 'text-teal-300',
    bar: 'bg-teal-500',
  },
  outreach: {
    Icon: Send,
    chip: 'bg-orange-500/15 ring-orange-500/25',
    icon: 'text-orange-300',
    bar: 'bg-orange-500',
  },
  rate_card: {
    Icon: ReceiptText,
    chip: 'bg-lime-500/15 ring-lime-500/25',
    icon: 'text-lime-300',
    bar: 'bg-lime-500',
  },
  calendar_event: {
    Icon: CalendarDays,
    chip: 'bg-pink-500/15 ring-pink-500/25',
    icon: 'text-pink-300',
    bar: 'bg-pink-500',
  },
  knowledge: {
    Icon: Brain,
    chip: 'bg-cyan-500/15 ring-cyan-500/25',
    icon: 'text-cyan-300',
    bar: 'bg-cyan-500',
  },
  pipeline: {
    Icon: BarChart3,
    chip: 'bg-violet-500/15 ring-violet-500/25',
    icon: 'text-violet-300',
    bar: 'bg-violet-500',
  },
  alert: {
    Icon: Bell,
    chip: 'bg-rose-500/15 ring-rose-500/25',
    icon: 'text-rose-300',
    bar: 'bg-rose-500',
  },
  idea: {
    Icon: Lightbulb,
    chip: 'bg-yellow-500/15 ring-yellow-500/25',
    icon: 'text-yellow-300',
    bar: 'bg-yellow-500',
  },
  club: {
    Icon: Users,
    chip: 'bg-indigo-500/15 ring-indigo-500/25',
    icon: 'text-indigo-300',
    bar: 'bg-indigo-500',
  },
  introduction: {
    Icon: Handshake,
    chip: 'bg-teal-500/15 ring-teal-500/25',
    icon: 'text-teal-300',
    bar: 'bg-teal-500',
  },
  activity: {
    Icon: PhoneCall,
    chip: 'bg-sky-500/15 ring-sky-500/25',
    icon: 'text-sky-300',
    bar: 'bg-sky-500',
  },
  note: {
    Icon: NotebookPen,
    chip: 'bg-slate-500/15 ring-slate-500/25',
    icon: 'text-slate-300',
    bar: 'bg-slate-500',
  },
  reminder: {
    Icon: Bell,
    chip: 'bg-amber-500/15 ring-amber-500/25',
    icon: 'text-amber-300',
    bar: 'bg-amber-500',
  },
  invoice: {
    Icon: ReceiptText,
    chip: 'bg-emerald-500/15 ring-emerald-500/25',
    icon: 'text-emerald-300',
    bar: 'bg-emerald-500',
  },
  credential: {
    Icon: ShieldCheck,
    chip: 'bg-cyan-500/15 ring-cyan-500/25',
    icon: 'text-cyan-300',
    bar: 'bg-cyan-500',
  },
  content_idea: {
    Icon: Lightbulb,
    chip: 'bg-fuchsia-500/15 ring-fuchsia-500/25',
    icon: 'text-fuchsia-300',
    bar: 'bg-fuchsia-500',
  },
  content_slot: {
    Icon: PenLine,
    chip: 'bg-violet-500/15 ring-violet-500/25',
    icon: 'text-violet-300',
    bar: 'bg-violet-500',
  },
  // Sky, not violet: a draft is pre-decision scratch, and reading as a sibling of
  // the content_slot it might one day become would blur that.
  draft: {
    Icon: FileText,
    chip: 'bg-sky-500/15 ring-sky-500/25',
    icon: 'text-sky-300',
    bar: 'bg-sky-500',
  },
  content_session: {
    Icon: CalendarDays,
    chip: 'bg-violet-500/15 ring-violet-500/25',
    icon: 'text-violet-300',
    bar: 'bg-violet-500',
  },
};

const FALLBACK_VISUAL: EntityVisual = {
  Icon: Sparkles,
  chip: 'bg-primary/15 ring-primary/25',
  icon: 'text-primary',
  bar: 'bg-primary',
};

export function entityVisual(entityType: EntityType): EntityVisual {
  return ENTITY_VISUALS[entityType] ?? FALLBACK_VISUAL;
}
